import { Injectable } from '@angular/core';
import { collection, doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { from, Observable, of, throwError } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take, tap } from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class StoreSyncService {

  private lastStoreUpdateTimestamp = 0;
  private readonly UPDATE_THROTTLE_MS = 60000; // 1 minute throttle
  private readonly STALE_DATA_THRESHOLD_MS = 3600000; // 1 hour

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private authService: AuthService,
    private authStateService: AuthStateService,
  ) {}
  
  createSyncListener(firestorePath: string, methodName: string, isCollection: boolean = false): Observable<any> {
      return this.catchService.isOnline$.pipe(
        filter((online) => online === true),
        // ✅ FIX: Gate on customTokenReady$ before opening the onSnapshot listener
        // to prevent permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
        switchMap(() => this.authStateService.customTokenReady$.pipe(take(1))),
        switchMap(
          () =>
            new Observable((observer) => {
              const handleSnapshotError = (error: unknown) => {
                if ((error as { code?: string })?.code === "permission-denied") {
                  observer.next(null);
                  return;
                }
                observer.error(error);
              };

              if (isCollection) {
                const collectionRef = collection(this.firestore, firestorePath);
                const unsub = onSnapshot(
                  collectionRef,
                  (snapshot) => {
                    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                    observer.next(data[0]);
                  },
                  handleSnapshotError
                );
                return () => unsub();
              } else {
                const docRef = doc(this.firestore, firestorePath);
                const unsub = onSnapshot(
                  docRef,
                  (snapshot) => {
                    const data = snapshot.data();
                    observer.next(data);
                  },
                  handleSnapshotError
                );
                return () => unsub();
              }
            }),
        ),
        tap((data: Data) => {
          if (data) {
            this.userMainPage().pipe(
              take(1),
              tap((userMainPageData: Data) => {
                const now = Date.now();
                
                // Only update if data is significantly stale and throttle is met
                if (data?.update_timestamp?.toDate && userMainPageData?.update_timestamp?.toDate) {
                  const timeDiff = data.update_timestamp.toDate().getTime() - userMainPageData.update_timestamp.toDate().getTime();
                  const shouldUpdate = timeDiff > this.STALE_DATA_THRESHOLD_MS && 
                                      (now - this.lastStoreUpdateTimestamp) > this.UPDATE_THROTTLE_MS;
                  
                  if (shouldUpdate) {
                    this.lastStoreUpdateTimestamp = now;
                    // Schedule update in the background without blocking
                    setTimeout(() => {
                      this.authService.getUid$("StoreRepositoryService").pipe(
                        take(1),
                        tap((userId) => {
                          if (!userId) return;
                          const req = {
                            userId: userId,
                            page: "store",
                            method: methodName,
                          };
                          // Fire-and-forget - don't wait for response
                          this.authService
                            .updateMainPages(req)
                            .pipe(take(1))
                            .subscribe({
                              error: (err) => this.authService.errorHandlerLogs('Background store promotion update failed:', err)
                            });
                        })
                      ).subscribe();
                    }, 0);
                  }
                }
              })
            ).subscribe();
          }
        }),
        shareReplay(1),
      );
    }

   userMainPage(): Observable<Data> {
      return this.authService.getUid$("StoreRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) return of(null);
          const documentRef = doc(this.firestore, `/users/${userId}/main_pages/store`);
          // Gate the user-private read until the custom token is ready so this
          // helper cannot race auth on iOS/Safari (LOTTOSOCIAL-PWA-1MEE).
          return this.authStateService.customTokenReady$.pipe(
            take(1),
            switchMap(() => from(getDoc(documentRef)).pipe(
              map((res: any) => res?.data()),
              catchError((err) => {
                if ((err as { code?: string })?.code === "permission-denied") {
                  return of(null);
                }
                return throwError(() => err);
              })
            ))
          );
        }));
    }
}
